import { useState, type ReactNode } from "react";
import { MoreInformationSection } from "../sections/MoreInformationSection";
import type { RunRecordBodyProps } from "../../runRecord/RunRecordBody";
import type { DrawerSectionMap } from "./types";
import HitlSideActions, {
  type SideActionResult,
} from "../../components/HitlSideActions";
import { renderInlineBold } from "../../renderInlineBold";
import { NEEDS_YOU_LABEL } from "~/common/workflows/statusLabels";
import { isWaitingOnPerson } from "~/common/workflows/statusPill";

/**
 * PendingEdocsDrawerBody — the shared carrier-evidence decision surface for an
 * instance parked in ``pending_edocs``. Shared by ora's WorkflowDetailDrawer
 * and the Outlook plugin's InstanceDetailDrawer.
 *
 * ONE decision identity, TWO branches, both rendered from the SAME
 * ``hitlQuestion`` payload with no host-specific fork (design Constraint 20):
 *
 * - **carrier branch** (``evidence_source !== "am"``) — the calm "status, no
 *   action needed" card. The AM has nothing to do: the run resumes on its own
 *   when the carrier's documents land. Affordances are the "Check now" side
 *   action, a confirm-first Reject, and — new — a confirm-first, visually
 *   SECONDARY take-over ("I'll supply the documents myself").
 *
 * - **AM branch** (``evidence_source === "am"``) — the run waits for the AM's
 *   own documents. The uploader is the host's ``decisionCard``
 *   (``CarrierEvidencePanel``): the shared ``carrierEvidenceCandidateFor``
 *   gives every such wait a card in BOTH hosts, so this body draws no uploader
 *   of its own (story 28633 — it used to, and the same wait then had two
 *   different uploaders depending on the run). "Check now" no longer applies
 *   and the substrate drops it from the payload, so ``HitlSideActions``
 *   renders nothing.
 *
 * **The take-over is NOT an ``x-action``** and is never rendered by
 * ``HitlSideActions`` — it is a confirm-first control drawn here, never the
 * default or ``autoFocus``. ``HitlSideActions`` stays the Check-now renderer
 * only; no id filtering, because that id is not in the payload.
 *
 * Every action rides the ONE substrate answer transport the hosts already
 * use (``POST …/resume`` → ``resume_workflow``). There is no second product
 * route and no host-local resolution path (design Constraint 17).
 */

export interface PendingEdocsDrawerBodyProps {
  sections: DrawerSectionMap;
  /** The gate's HITL question — carries the branch, the ``x-actions``
   *  ("Check now") on the carrier branch, and the required-evidence keys on
   *  the AM branch. */
  hitlQuestion?: Record<string, unknown> | null;
  /** Runs a declared side action (Check now) by its endpoint. */
  onSideAction?: (endpoint: string) => Promise<SideActionResult>;
  /** Submits the reject decision (terminates the workflow). */
  onReject?: () => void;
  /** True while a reject submit is in flight — disables the confirm button. */
  rejectPending?: boolean;
  /** Error from the most recent failed reject submit — shown inline next to
   *  the confirm controls (the top-level action-error banner is suppressed
   *  whenever a hitlQuestion is present, which is always true here). */
  rejectError?: string | null;
  /**
   * Changes which side supplies the evidence (the carrier branch's take-over).
   * The host maps the action onto its EXISTING resume submitter exactly as it
   * already does for reject — ``(action) => handleResume({ decision: action })``.
   * Nothing here needs a new transport.
   */
  onEvidenceSourceChange?: (
    action: "takeover" | "wait_for_carrier",
  ) => void | Promise<void>;
  /** True while a source-change submit is in flight. */
  evidencePending?: boolean;
  /**
   * The phase / milestone main stream (running-drawer). A ReactNode, not a
   * snapshot: the bodies stay presentation-only and none of them imports
   * `phaseModel.ts`, so the adapter keeps its single-reader rule.
   *
   * Rendered FIRST when present, and it SUPPRESSES the flat steps chip it
   * replaces.
   */
  phaseStream?: ReactNode | null;
  /**
   * The step view (story 26739) — the "what do I do right now" pane for this
   * moment in the run. A ReactNode, not a model: the bodies stay
   * presentation-only, so the host derives the view from `push.status` on
   * every render and hands the finished node down.
   *
   * When present it takes the hero position and suppresses the AM branch's
   * "Needs You" banner and the flat steps chip. It does NOT replace the decision /
   * status region below: that region owns "Check now", the confirm-first
   * Reject and — on the AM branch — THE evidence uploader, and there is
   * exactly one of those in the product. So the packet view is rendered ABOVE
   * it and the host passes it no uploader of its own. Deliberate deviation
   * from artboard (4), which draws the dropzone inside the pane: a second
   * dropzone is the failure this body already fixed once.
   *
   * Default `undefined` reproduces today's markup exactly.
   */
  stepView?: ReactNode | null;
  /**
   * The carrier-evidence decision card (running-drawer). Present ONLY when the
   * run carries an `evidence_decision`, never gated on a status literal —
   * gating on the parked status makes the early-override path unreachable
   * (AC-6). Absent prop reproduces today's markup byte for byte.
   */
  decisionCard?: ReactNode | null;
  /**
   * The run's warning lines, each with its own Acknowledge (story 28633) — the
   * host's `WarningAcknowledgementList`, the SAME control the finished bodies
   * render, so a line is acknowledgeable while the run is still waiting on
   * documents. Drawn after the decision region, before the request.
   * Absent/null draws nothing.
   */
  warningLines?: ReactNode | null;
  /** The run record shown behind the more-information entry point. The SAME
   *  record the standalone /workflows/runs/<id> page renders. */
  record: RunRecordBodyProps;
  /** Copy-id / kill-session links — More information pane only. */
  debugActions?: ReactNode | null;
  /** Whether the more-information pane is open. Owned by the top-level
   *  drawer component (survives step/phase updates); the entry-point button
   *  itself lives in the sticky footer, not here. */
  moreInfoOpen?: boolean;
  /** Called when the pane's own back button is clicked. */
  onMoreInfoClose?: () => void;
}

function getString(
  q: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const v = q?.[key];
  return typeof v === "string" && v ? v : undefined;
}

export function PendingEdocsDrawerBody({
  sections,
  hitlQuestion,
  onSideAction,
  onReject,
  rejectPending = false,
  rejectError,
  onEvidenceSourceChange,
  evidencePending = false,
  phaseStream,
  stepView,
  decisionCard,
  warningLines,
  record,
  debugActions,
  moreInfoOpen = false,
  onMoreInfoClose,
}: PendingEdocsDrawerBodyProps) {
  const [confirmingReject, setConfirmingReject] = useState(false);
  const [confirmingTakeover, setConfirmingTakeover] = useState(false);

  // The AM-branch keys are ABSENT on the carrier branch by design: their key
  // NAMES contain "required", and the must_pass probes grep the raw
  // hitl_question::text — keys included — to prove the pre-takeover body never
  // says it. So every read here treats absence as "not required"/empty.
  // Never `=== false`.
  const amBranch = hitlQuestion?.["evidence_source"] === "am";
  // The pill's question, asked of the ONE shared derivation (story 28633): the
  // AM branch waits on the Servicer's OWN documents, so it is Needs You.
  const waitingOnYou = isWaitingOnPerson("pending_edocs", { hitlQuestion });

  // Split into named pieces so the running-drawer decision card can REPLACE
  // the copy while the two live affordances are re-parented rather than
  // deleted. `checkNowBlock` and the Reject block are children of the calm
  // card's container today, so a naive replacement would remove both with it.
  const checkNowBlock: ReactNode =
    hitlQuestion && onSideAction ? (
      <HitlSideActions hitlQuestion={hitlQuestion} onAction={onSideAction} />
    ) : null;

  const carrierStatusCard: ReactNode = (
    <>
      <p className="text-sm font-semibold text-primary-gray-light-800">
        Waiting on the carrier
      </p>
      <p className="text-xs text-primary-gray-light-700">
        Waiting for the carrier to deliver the updated policy documents into
        Epic. We check every day automatically, and this resumes on its own
        when they arrive — there&rsquo;s nothing you need to do here.
      </p>
      <p className="text-2xs text-primary-gray-light-500">
        This can take up to 14 days.
      </p>

      {/* "Check now" side action (from the gate's x-actions). Absent on the
          AM branch because the substrate drops x-actions there. */}
      {checkNowBlock}

      {/* Take over — a THIRD, visually secondary option. Never the default,
          never autoFocus, and confirm-first because it makes supplying the
          declarations page the AM's job. */}
      {onEvidenceSourceChange &&
        (confirmingTakeover ? (
          <div
            className="mt-1 flex items-center gap-2 flex-wrap"
            role="dialog"
            aria-label="Confirm taking over the documents"
            data-testid="pending-edocs-takeover-confirm"
          >
            <span className="text-2xs text-primary-gray-light-700">
              Take this over? You&rsquo;ll need to supply the declarations page
              yourself, and we&rsquo;ll stop waiting on the carrier.
            </span>
            <button
              type="button"
              onClick={() => setConfirmingTakeover(false)}
              className="lnk-gray text-2xs px-1.5 py-0.5"
              data-testid="pending-edocs-takeover-cancel"
            >
              Keep waiting
            </button>
            <button
              type="button"
              disabled={evidencePending}
              onClick={() => {
                void onEvidenceSourceChange("takeover");
              }}
              className="px-2 py-0.5 rounded border border-primary-gray-light-300 bg-base-white text-primary-gray-light-800 text-2xs font-semibold hover:bg-primary-gray-light-50 disabled:opacity-60"
              data-testid="pending-edocs-takeover-yes"
            >
              {evidencePending ? "…" : "Yes, I'll supply them"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingTakeover(true)}
            className="mt-1 self-start text-2xs text-primary-gray-light-700 hover:underline"
            data-testid="pending-edocs-takeover"
          >
            I&rsquo;ll supply the documents myself
          </button>
        ))}
    </>
  );

  // The AM branch's words when no decision card was passed. Words only: the
  // uploader is the card (`CarrierEvidencePanel`), and the shared
  // `carrierEvidenceCandidateFor` gives every AM documents wait one in both
  // hosts, so this body never draws a second uploader (story 28633).
  const amEvidenceCard: ReactNode = (
    <>
      <p className="text-sm font-semibold text-primary-gray-light-800">
        {renderInlineBold(
          getString(hitlQuestion, "question") ?? "Supply the Declarations Page",
        )}
      </p>
      <p className="text-xs text-primary-gray-light-700">
        {renderInlineBold(
          getString(hitlQuestion, "message") ??
            getString(hitlQuestion, "pending_action") ??
            "You took this over, so the declarations page is required before this can finish.",
        )}
      </p>
    </>
  );

  const rejectBlock: ReactNode = (
    <>
      {/* Confirm-first Reject — terminal, so guard against a stray click.
          Offered on BOTH branches. */}
      {onReject &&
        (confirmingReject ? (
          <div
            className="mt-1 flex items-center gap-2"
            role="dialog"
            aria-label="Confirm reject"
            data-testid="pending-edocs-reject-confirm"
          >
            <span className="text-2xs text-primary-gray-light-700">
              Reject this change? It won&rsquo;t be applied.
            </span>
            <button
              type="button"
              onClick={() => setConfirmingReject(false)}
              className="lnk-gray text-2xs px-1.5 py-0.5"
              data-testid="pending-edocs-reject-keep"
            >
              Keep
            </button>
            <button
              type="button"
              disabled={rejectPending}
              onClick={onReject}
              className="px-2 py-0.5 rounded bg-primary-error-600 text-base-white text-2xs font-semibold hover:bg-primary-error-700 disabled:opacity-60"
              data-testid="pending-edocs-reject-yes"
            >
              {rejectPending ? "…" : "Yes, reject"}
            </button>
            {rejectError && (
              <span
                role="alert"
                className="text-2xs text-primary-error-700"
                data-testid="pending-edocs-reject-error"
              >
                {rejectError}
              </span>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmingReject(true)}
            className="mt-1 self-start text-2xs text-primary-error-700 hover:underline"
            data-testid="pending-edocs-reject"
          >
            Reject if this change is no longer needed
          </button>
        ))}
    </>
  );

  return (
    <>
      {/* Waiting for the Servicer's OWN documents is Needs You in amber, like
          every other wait on them (story 28633, ruled 2026-10-08) — the grid
          and the header say the same. The carrier branch has no banner: its
          calm status card below is the whole story. */}
      {stepView ? null : phaseStream && waitingOnYou ? (
        <div
          data-testid="pending-edocs-banner"
          role="status"
          className="mb-2 flex items-center gap-2 rounded-md border border-primary-warning-300 bg-primary-warning-50 px-3 py-2"
        >
          <span
            aria-hidden
            className="inline-block h-2 w-2 shrink-0 rounded-full bg-primary-warning-600"
          />
          <p className="text-2xs font-semibold uppercase tracking-wide text-primary-warning-800">
            {NEEDS_YOU_LABEL}
          </p>
        </div>
      ) : null}

      {/* The phase stream is the ALWAYS-ON progress display (operator
          directive 2026-09-16): a step view no longer displaces it. Both
          render, stream FIRST, so run progress stays the first thing in the
          body and the step view's reassurance sits under it. */}
      {phaseStream ?? null}
      {stepView ?? null}

      {decisionCard ? (
        /* running-drawer: the decision card REPLACES the calm status card
           entirely (AC-5 fails on a card PLUS a separate status element). The
           gate's two live affordances — "Check now" and the confirm-first
           Reject — plus the AM branch's uploader are RE-PARENTED into the same
           decision REGION, drawn as a continuation of the card, not deleted.
           They are children of that one region, so AC-7's four-region
           enumeration still counts exactly four. */
        <div
          className="mb-2 flex flex-col"
          data-testid="carrier-evidence-decision"
          data-evidence-source={amBranch ? "am" : "carrier"}
        >
          {decisionCard}
          <div
            className="-mt-2 rounded-b-lg border border-t-0 border-primary-gray-light-200 bg-base-white px-3 pb-3 pt-1 flex flex-col gap-1"
            data-testid="carrier-evidence-gate-actions"
          >
            {checkNowBlock}
            {/* NO uploader here. `CarrierEvidencePanel` (the decisionCard) owns
                the one evidence uploader. This slot used to render a SECOND
                one — two dropzones and a "Submit declarations page" button,
                stacked under the card's own dropzone and "Stage evidence" — so
                the AM was offered two ways to hand over the same PDF. */}
            {rejectBlock}
          </div>
        </div>
      ) : (
        <div
          className="rounded-lg border border-primary-gray-light-200 bg-base-white p-3 mb-2 flex flex-col gap-1"
          data-testid="pending-edocs-status"
          data-evidence-source={amBranch ? "am" : "carrier"}
        >
          {amBranch ? amEvidenceCard : carrierStatusCard}

          {rejectBlock}
        </div>
      )}

      {/* Story 28633: the run's warning lines, acknowledgeable while live. */}
      {warningLines ?? null}

      {sections.request ?? null}

      <MoreInformationSection
        record={record}
        open={moreInfoOpen}
        onClose={() => onMoreInfoClose?.()}
        extraBlocks={
          <>
            {debugActions ?? null}
          </>
        }
      />
    </>
  );
}

export default PendingEdocsDrawerBody;
